<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <header class="dialog-header">
        <h2>Edit Validation Session</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </header>

      <div class="dialog-body">
        <div class="form-group">
          <label for="session-name">Session Name</label>
          <input
            id="session-name"
            v-model="editedSession.name"
            type="text"
            class="form-input"
            placeholder="Session name"
          />
        </div>

        <div class="form-group">
          <label for="session-description">Description</label>
          <textarea
            id="session-description"
            v-model="editedSession.description"
            class="form-textarea"
            placeholder="Description"
            rows="4"
          />
        </div>
      </div>

      <footer class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-save"
          :disabled="!isFormValid"
          @click="handleSave()"
        >
          Save
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'ValidationSessionEditDialog',
  props: {
    selectedSessions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['edit-session'],
  data() {
    return {
      isOpen: false,
      editedSession: {
        id: null,
        name: '',
        description: '',
      },
      originalSession: null,
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
    };
  },
  computed: {
    isFormValid() {
      return this.editedSession.name && this.editedSession.name.trim().length > 0;
    },
  },
  methods: {
    openModal(session) {
      if (!session) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'No session selected',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      this.originalSession = JSON.parse(JSON.stringify(session));
      this.editedSession = {
        id: session.id,
        name: session.docker_task?.name || '',
        description: session.description || '',
      };
      this.isOpen = true;
    },
    handleCancel() {
      this.isOpen = false;
    },
    async handleSave() {
      if (!this.isFormValid) return;

      try {
        const params = {
          validate_session_id: this.editedSession.id,
          name: this.editedSession.name,
          description: this.editedSession.description,
        };

        const resp = await this.dataConnect.updateValidationSession(params);

        if (resp && !resp.error && resp.result) {
          $toast.open({
            queue: true,
            duration: 3000,
            message: 'Validation session updated successfully',
            type: 'success',
            position: 'top',
          });

          // Update editedSession with returned values
          this.editedSession = {
            id: resp.result.id,
            name: resp.result.docker_task?.name || resp.result.name || this.editedSession.name,
            description: resp.result.description || this.editedSession.description,
          };

          this.$emit('edit-session', this.editedSession);
          this.isOpen = false;
        } else {
          throw new Error(resp?.error?.message || 'Failed to update session');
        }
      } catch (error) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Error: ${error.message}`,
          type: 'error',
          position: 'top',
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/css/themes/auzone/buttons.scss';
.dialog-body {
  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 14px;
      font-weight: 600;
      color: var(--body-text-color);
    }
  }
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: var(--body-text-color);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 170px;
}

</style>
